import { Link } from "react-router-dom";
import { Facebook, Instagram, Youtube, Music2, Star, Mail, MapPin } from "lucide-react";
import {
  business,
  social,
  services,
  safeServices,
  navLinks,
} from "@/data/siteConfig";
import { VelascoLogo } from "@/components/VelascoLogo";
import { CallButton, PhoneDisplay } from "@/components/CallButton";

const socialIcon = {
  Facebook,
  Instagram,
  YouTube: Youtube,
  TikTok: Music2,
  Yelp: Star,
};

const FooterCol = ({ title, children }) => (
  <div>
    <h4 className="mb-4 font-heading text-xs font-bold uppercase tracking-[0.2em] text-gold">
      {title}
    </h4>
    <ul className="space-y-2.5">{children}</ul>
  </div>
);

const FooterLink = ({ to, children }) => (
  <li>
    <Link
      to={to}
      className="text-sm text-steel transition-colors duration-200 hover:text-white"
    >
      {children}
    </Link>
  </li>
);

export const Footer = () => (
  <footer className="grain relative border-t border-white/10 bg-ink" data-testid="footer">
    {/* CTA band */}
    <div className="relative z-10 border-b border-white/10">
      <div className="mx-auto flex max-w-[1440px] flex-col items-start justify-between gap-8 px-5 py-14 md:flex-row md:items-center md:px-8 md:py-16">
        <div>
          <div className="mb-3 flex items-center gap-3 font-heading text-xs font-bold uppercase tracking-[0.2em] text-gold">
            <span className="h-px w-8 bg-gold" /> Talk to Jose directly
          </div>
          <h2 className="max-w-xl font-heading text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
            Locked out or need a safe opened?
          </h2>
          <p className="mt-3 max-w-lg text-steel">
            No online booking — every job is different. Call and we'll talk it through.
          </p>
        </div>
        <div className="flex flex-col items-start gap-4">
          <PhoneDisplay size="text-3xl md:text-4xl" testid="footer-phone" />
          <CallButton size="lg" testid="footer-call-btn" />
        </div>
      </div>
    </div>

    {/* Link grid */}
    <div className="relative z-10 mx-auto max-w-[1440px] px-5 py-14 md:px-8">
      <div className="grid grid-cols-2 gap-10 md:grid-cols-4 lg:grid-cols-6">
        <div className="col-span-2">
          <VelascoLogo />
          <p className="mt-5 max-w-xs text-sm leading-relaxed text-steel">
            {business.shortPitch}
          </p>
          <p className="mt-3 font-heading text-xs font-bold uppercase tracking-[0.18em] text-gold">
            {business.credentials}
          </p>
          <div className="mt-6 flex gap-3">
            {social.map((s) => {
              const Icon = socialIcon[s.name] || Star;
              return (
                <a
                  key={s.name}
                  href={s.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={s.name}
                  data-testid={`social-${s.name.toLowerCase()}`}
                  className="flex h-10 w-10 items-center justify-center rounded-sm border border-white/10 text-steel-light transition-colors duration-200 hover:border-gold/50 hover:text-gold"
                >
                  <Icon className="h-4 w-4" />
                </a>
              );
            })}
          </div>
        </div>

        <FooterCol title="Services">
          {services.slice(0, 6).map((s) => (
            <FooterLink key={s.slug} to="/services">
              {s.title}
            </FooterLink>
          ))}
        </FooterCol>

        <FooterCol title="Safe Services">
          {safeServices.slice(0, 6).map((s) => (
            <FooterLink key={s.slug} to="/safe-services">
              {s.title}
            </FooterLink>
          ))}
        </FooterCol>

        <FooterCol title="Company">
          {navLinks
            .filter((l) => !["/", "/services", "/safe-services"].includes(l.path))
            .map((l) => (
              <FooterLink key={l.path} to={l.path}>
                {l.label}
              </FooterLink>
            ))}
        </FooterCol>

        <FooterCol title="Contact">
          <li className="flex items-start gap-2 text-sm text-steel">
            <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
            {business.city}, {business.state}
          </li>
          <li>
            <a
              href={`tel:${business.phoneTel}`}
              className="text-sm text-steel transition-colors hover:text-white"
            >
              {business.phone}
            </a>
          </li>
          <li className="flex items-start gap-2">
            <Mail className="mt-0.5 h-4 w-4 shrink-0 text-gold" />
            <a
              href={`mailto:${business.email}`}
              className="break-all text-sm text-steel transition-colors hover:text-white"
            >
              {business.email}
            </a>
          </li>
        </FooterCol>
      </div>
    </div>

    {/* bottom bar */}
    <div className="relative z-10 border-t border-white/10">
      <div className="mx-auto flex max-w-[1440px] flex-col items-center justify-between gap-3 px-5 py-6 text-center md:flex-row md:px-8 md:text-left">
        <p className="text-xs text-steel">
          © {new Date().getFullYear()} {business.name}. Based in {business.city}, {business.state}.
        </p>
        <p className="text-xs text-steel/70">
          Serving {business.region} — call to confirm availability for your area.
        </p>
      </div>
    </div>
  </footer>
);
